import { Alert, Snackbar } from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { StoreType } from "../../../../utils/types";
import useWorkerSchedulerData from "../../controllers/use-worker-scheduler-data";
import {
	closeTransitionDialog,
	resetUpdateWorkerAvailabilityStatus,
	updateWorkerAvailabilityStatus,
} from "../../store/worker-scheduler-slice";
import { DialogType } from "../../types/transition-types";
import CallOffDialog from "../dialogs/call-off-dialog";
import ConfirmationDialog from "../dialogs/confirmation-dialog";
import ReassignmentDialog from "../dialogs/reassignment-dialog";
import RemoveCallOffDialog from "../dialogs/remove-call-off-dialog";

const GlobalTransitionDialogManager: React.FC = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
  const currentUserId = useSelector((state: any) => state.user?.userData?.id);
	const {
		schedulingStatusesObj,
		updateAvailabilityStatusStatus,
		selectedLocation,
	} = useWorkerSchedulerData();
	const transitionDialog = useSelector(
		(state: StoreType) => state.workerScheduler.transitionDialog
	);

	const handleConfirmationDialogConfirm = useCallback(() => {
		const { toStatus, workerId, fromStatus, shiftId } = transitionDialog.data;

		dispatch(
			updateWorkerAvailabilityStatus({
				workerId,
				ftShiftId: shiftId,
				fromStatusCode: fromStatus,
				toStatusCode: toStatus,
			})
		);
	}, [dispatch, transitionDialog.data]);

	const handleRemoveCallOffConfirm = useCallback(() => {
		const { workerId, fromStatus, toStatus, shiftId } = transitionDialog.data;

		dispatch(
			updateWorkerAvailabilityStatus({
				workerId,
				ftShiftId: shiftId,
				fromStatusCode: fromStatus,
				toStatusCode: "RCOF",
				lastStatus: toStatus,
			})
		);
	}, [dispatch, transitionDialog.data]);

	const handleReassignmentConfirm = useCallback(
		(selectedJobWC: { jobId: number; workCenterId: number }) => {
			const { workerId, shiftId, fromStatus } = transitionDialog.data;

			const payload = {
				workerId,
				ftShiftId: shiftId,
				toStatusCode: "RAS",
				fromStatusCode: fromStatus,
				callOffParams: null,
				reAssignParams: {
					jobId: selectedJobWC.jobId,
					workcenterId: selectedJobWC.workCenterId,
					locationId: selectedLocation,
				},
			};

			dispatch(updateWorkerAvailabilityStatus(payload));
		},
		[dispatch, transitionDialog.data, selectedLocation]
	);

	const handleCallOffConfirm = useCallback(
		(callOffData: {
			ftRequestTypeId: number;
			ftLeaveTypes: {
        leaveTypeId: number;
        requestedHours: number;
      }[];
			ftSupervisorComment: string;
			ftStartDateTime: string;
			ftEndDateTime: string;
			ftFromStatus: string;
		}) => {
			const { workerId, shiftId } = transitionDialog.data;

			const payload = {
				workerId,
				ftShiftId: shiftId,
				toStatusCode: "COF",
				fromStatusCode: callOffData.ftFromStatus,
				reAssignParams: null,
				callOffParams: {
					requestTypeId: callOffData.ftRequestTypeId,
					leaveTypes: callOffData.ftLeaveTypes,
					supervisorComment: callOffData.ftSupervisorComment,
					startDate: callOffData.ftStartDateTime,
					endDate: callOffData.ftEndDateTime,
				},
			};

			dispatch(updateWorkerAvailabilityStatus(payload));
		},
		[dispatch, transitionDialog.data]
	);

	const handleDialogCancel = useCallback(() => {
		dispatch(closeTransitionDialog());
	}, [dispatch]);

	const renderConfirmationDialog = () => {
		if (
			transitionDialog.dialogType !== DialogType.CONFIRMATION ||
			!transitionDialog.isOpen
		) {
			return null;
		}

		const { transition, workerFullName, workerPrimaryJobName, workerPrimaryWorkcenterName } = transitionDialog.data;

		const title = `${transition.title} - ${workerFullName}`;

    const message = transition.toStatus === "SCH" ?
      t("full-time-worker-scheduler_reset-job-message", {
        job: workerPrimaryJobName,
        workcenter: workerPrimaryWorkcenterName,
      })
      : transition.message;

    let key = undefined;
    let canShowDialog = true;
    switch (transition.toStatus) {
      case "RDU":
        key = "doNotRemindMeRestrictedDutyStatusChange";
        canShowDialog = localStorage.getItem(`doNotRemindMeRestrictedDutyStatusChange-${currentUserId}`) !== "true";
        break;
      case "TRA":
        key = "doNotRemindMeTrainingStatusChange";
        canShowDialog = localStorage.getItem(`doNotRemindMeTrainingStatusChange-${currentUserId}`) !== "true";
        break;
      default:
        key = undefined;
    }

    if (canShowDialog) {
      return (
        <ConfirmationDialog
          open={transitionDialog.isOpen}
          title={title}
          doNotRemindMeCheckBoxKey={key}
          message={
            message || t("full-time-worker-scheduler_confirm-status-change")
          }
          onConfirm={handleConfirmationDialogConfirm}
          onCancel={handleDialogCancel}
          loading={updateAvailabilityStatusStatus === "pending"}
        />
      );
    } else {
      if (updateAvailabilityStatusStatus === "idle") {
        handleConfirmationDialogConfirm();
      }
      return null;
    }
	};

	const renderRemoveCallOffDialog = () => {
		if (
			transitionDialog.dialogType !== DialogType.REMOVE_CALL_OFF ||
			!transitionDialog.isOpen
		) {
			return null;
		}

		const { toStatus, workerFullName } = transitionDialog.data;
		const previousStatusName =
			schedulingStatusesObj[toStatus]?.name ||
			t("full-time-worker-scheduler_previous-status");

		return (
			<RemoveCallOffDialog
				open={transitionDialog.isOpen}
				workerFullName={workerFullName}
				previousStatusId={toStatus}
				previousStatusName={previousStatusName}
				onConfirm={handleRemoveCallOffConfirm}
				onCancel={handleDialogCancel}
				loading={updateAvailabilityStatusStatus === "pending"}
			/>
		);
	};

	const renderReassignmentDialog = () => {
		if (
			transitionDialog.dialogType !== DialogType.REASSIGNMENT ||
			!transitionDialog.isOpen
		) {
			return null;
		}

		const { workerFullName, shiftDetails, currentAssignment } =
			transitionDialog.data;

		return (
			<ReassignmentDialog
				open={transitionDialog.isOpen}
				workerFullName={workerFullName}
				shiftDetails={shiftDetails}
				onConfirm={handleReassignmentConfirm}
				onCancel={handleDialogCancel}
			/>
		);
	};

	const renderCallOffDialog = () => {
		if (
			transitionDialog.dialogType !== DialogType.CALL_OFF ||
			!transitionDialog.isOpen
		) {
			return null;
		}

		const { workerFullName, currentAssignment, workerId, fromStatus } = transitionDialog.data;

		return (
			<CallOffDialog
				open={transitionDialog.isOpen}
        workerId={workerId}
        fromStatus={fromStatus}
				workerFullName={workerFullName}
				currentAssignment={currentAssignment}
				onConfirm={handleCallOffConfirm}
				onCancel={handleDialogCancel}
			/>
		);
	};

	useEffect(() => {
		if (updateAvailabilityStatusStatus === "fulfilled") {
			dispatch(closeTransitionDialog());
		}
	}, [updateAvailabilityStatusStatus, dispatch]);

	return (
		<>
			{renderConfirmationDialog()}
			{renderRemoveCallOffDialog()}
			{renderReassignmentDialog()}
			{renderCallOffDialog()}

			{updateAvailabilityStatusStatus === "fulfilled" && (
				<Snackbar
					open={updateAvailabilityStatusStatus === "fulfilled"}
					autoHideDuration={3000}
					onClose={() => dispatch(resetUpdateWorkerAvailabilityStatus())}
					anchorOrigin={{ vertical: "top", horizontal: "center" }}
				>
					<Alert elevation={6} variant='filled' severity='success'>
						{t("full-time-worker-scheduler_status-updated-success")}
					</Alert>
				</Snackbar>
			)}
		</>
	);
};

export default GlobalTransitionDialogManager;
